import React, { useCallback, useRef, useEffect, useState } from "react";
import {
  Box,
  Button,
  Calendar,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  InputField,
  Modal,
  Radio,
  Toggle,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import utilsDocs from "../../shared/utils/docs";
import { DocInfo, FileLinkPolicy } from "../../types/files";
import utilsCommon from "../../shared/utils/common";
import { nanoid } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import usePolicyStore from "../../redux/dispatcher/usePolicyStore";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import { t } from "i18next";
const copy = require("clipboard-copy");

interface ShareLinkModalProps {
  open: boolean;
  onClose: () => void;
  docInfo?: DocInfo;
}

const ShareLinkModal: React.FC<ShareLinkModalProps> = ({
  open,
  onClose,
  docInfo,
}) => {
  const UNAUTH_LINK_TYPE = [
    { label: t("file.link.previewOnly"), key: "WEBUSING" },
    { label: t("file.link.downloadOnly"), key: "DOWNLOAD" },
    { label: t("file.previewNDownload"), key: "WEBUSING_AND_DOWNLOAD" },
  ];
  const toast = useToast();
  const nowDate = new Date();
  const serverTimezone = -nowDate.getTimezoneOffset() / 60;
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  /** Hook 정리 */
  const { policyState, setLinkFilePolicy, getLinkFilePolicy } =
    usePolicyStore();
  const { workgroupState, getWorkgroupDetail } = useWorkgroupStore();

  /** State 정리 */
  const [isMyDoc, changeIsMyDoc] = useState(false);
  const [linkInfo, changeLinkInfo] = useState<FileLinkPolicy>(); // 링크 정보 저장
  const [key, setKey] = useState(0); // state update용
  const [isPWChange, changeIsPWChange] = useState(false); // 비밀번호 수정 감지
  const [passwordInfo, changePasswordInfo] = useState({
    isOn: !!linkInfo?.fileShareLinkPolicy?.password,
    pw: linkInfo?.fileShareLinkPolicy?.password,
  }); // 비밀번호 관련 정보 isOn: toggle On, pw: 비밀번호
  const [expiredDateInfo, changeExpiredDateInfo] = useState<{
    isOn: boolean;
    date?: Date;
  }>({
    isOn:
      !!linkInfo?.fileShareLinkPolicy &&
      linkInfo?.fileShareLinkPolicy?.allowDays !== -1,
    date:
      !!linkInfo?.fileShareLinkPolicy &&
      linkInfo?.fileShareLinkPolicy?.allowDays !== -1
        ? new Date(
            dayjs()
              .add((linkInfo?.fileShareLinkPolicy?.allowDays ?? 1) - 1, "d")
              .valueOf()
          )
        : undefined,
  }); // 유효기간 관련 정보 isOn: toggle On, date: 유효기간
  const isAvailblePreview = utilsCommon.checkMenuAvailble("preview", docInfo);
  // &&
  // (!docInfo?.workgroup?.workgroupId ||
  // docInfo?.workgroup?.workgroupId === "NONEWORKGROUP"
  //   ? true
  //   : workgroupState?.workgroupDetail &&
  //     workgroupState?.workgroupDetail?.policy?.policy?.webView);

  /** useEffect 정리 */
  useEffect(() => {
    if (!docInfo) return;
    getLinkFilePolicy({
      workgroupId: docInfo?.workgroup.workgroupId,
      syncId: docInfo?.syncId,
    });
    changeIsMyDoc(
      docInfo?.masterUser?.userId || docInfo?.master?.userId
        ? (docInfo?.masterUser?.userId ?? docInfo?.master?.userId) ===
            loginDto.userInfo.userId
        : false
    );
  }, [docInfo]);
  useEffect(() => {
    if (!docInfo || workgroupState.workgroupDetail) return;
    if (docInfo?.workgroup.workgroupId !== "NONEWORKGROUP")
      getWorkgroupDetail({ workgroupId: docInfo?.workgroup.workgroupId });
  }, [workgroupState.workgroupDetail]);
  useEffect(() => {
    changeLinkInfo(policyState.fileLinkPolicy);
  }, [policyState.fileLinkPolicy]);
  useEffect(() => {
    changeExpiredDateInfo({
      isOn:
        !!linkInfo?.fileShareLinkPolicy &&
        linkInfo?.fileShareLinkPolicy?.allowDays > 0,
      date:
        !!linkInfo?.fileShareLinkPolicy &&
        linkInfo?.fileShareLinkPolicy?.allowDays > 0
          ? new Date(
              dayjs()
                .add((linkInfo?.fileShareLinkPolicy?.allowDays ?? 1) - 1, "d")
                .valueOf()
            )
          : undefined,
    });
    changePasswordInfo({
      isOn: !!linkInfo?.fileShareLinkPolicy?.password,
      pw: linkInfo?.fileShareLinkPolicy?.password,
    });
  }, [linkInfo]);
  /** 함수 정리 */
  /** url 링크 복사 */
  const handleCopyLink = () => {
    copy(linkInfo?.actionUrl);
    toast.toastMsg(nanoid(), t("file.link.copySuccess"), "success");
  };
  /** link 관련 정보 저장 */
  const handleSetFileLinkPolicy = (newKey: string, value: any) => {
    if (!linkInfo || !docInfo) return;
    changeLinkInfo({
      ...linkInfo,
      fileShareLinkPolicy: { ...linkInfo.fileShareLinkPolicy, [newKey]: value },
    });
    setLinkFilePolicy({
      syncId: docInfo?.syncId,
      workgroupId: docInfo?.workgroup.workgroupId,
      fileShareLinkPolicy: {
        ...linkInfo.fileShareLinkPolicy,
        allowDaysTimeZone: serverTimezone,
        authType:
          newKey === "password"
            ? value.length > 0
              ? "PASSWORD"
              : "NONE"
            : newKey === "authType"
            ? value
            : linkInfo.fileShareLinkPolicy.authType,
        allowDays:
          newKey === "authType" && value === "LOGIN"
            ? -1
            : newKey === "allowDays"
            ? value
            : linkInfo.fileShareLinkPolicy.allowDays,
        password:
          newKey === "authType" && value === "LOGIN"
            ? ""
            : newKey === "password"
            ? value
            : linkInfo.fileShareLinkPolicy.password,
        [newKey]: value,
      },
    });
    changeIsPWChange(newKey === "password" && value.length > 0);
    setKey(key + 1);
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("file.action.shareLink")}
      width={540}
      isFooter={false}
      classes="shareLinkModal"
      body={
        <Box direction={"column"}>
          <Box classes="shareLinkModal-docInfo">
            {utilsDocs.renderDocIcon(docInfo?.contentName, 40)}
            <Box
              direction="column"
              width={"calc(100% - 52px)"}
              classes="mg ml-12"
            >
              <Tooltip message={docInfo?.contentName}>
                <Typography type="body2_rg" ellipsis>
                  {docInfo?.contentName}
                </Typography>
              </Tooltip>
              <Box style={{ marginTop: "5px" }}>
                <Typography
                  type="caption_rg"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {`${utilsDocs.fileSizeFormat(docInfo?.fileSize)} • `}
                </Typography>
                <Tooltip
                  message={
                    docInfo?.masterUser?.userName ??
                    docInfo?.master?.userName ??
                    docInfo?.masterUser?.userId ??
                    docInfo?.master?.userId
                  }
                  maxWidth={213}
                >
                  <Typography
                    type="caption_rg"
                    classes="mg ml-4 mr-4"
                    ellipsis
                    exactColor={getColor("COLOR_TEXT_SECONDARY")}
                  >
                    {docInfo?.masterUser?.userName ??
                      docInfo?.master?.userName ??
                      docInfo?.masterUser?.userId ??
                      docInfo?.master?.userId}
                  </Typography>
                </Tooltip>
                <Typography
                  type="caption_rg"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {` • ${utilsCommon.renderDateFormat(
                    docInfo?.revisionDate ?? docInfo?.updateTime
                  )}`}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box classes="mg mt-20" direction="column">
            {isMyDoc && (
              <Box direction="column" style={{ marginBottom: "20px" }}>
                <Typography type="subtitle2">
                  {t("file.link.linkType")}
                </Typography>
                <Box classes={"shareLinkModal-linkType"}>
                  <Radio
                    label={t("common.unVerified")}
                    check={linkInfo?.fileShareLinkPolicy?.authType !== "LOGIN"}
                    onClick={() => handleSetFileLinkPolicy("authType", "NONE")}
                  />
                  <Radio
                    label={t("common.verified")}
                    check={linkInfo?.fileShareLinkPolicy?.authType === "LOGIN"}
                    onClick={() => handleSetFileLinkPolicy("authType", "LOGIN")}
                  />
                </Box>
                {linkInfo?.fileShareLinkPolicy?.authType !== "LOGIN" && (
                  <Dropdown
                    btnStyle="line"
                    dropdownMaxWidth={200}
                    width={200}
                    showValue
                    placeholder="s"
                    value={
                      UNAUTH_LINK_TYPE.filter(
                        (item) =>
                          linkInfo?.fileShareLinkPolicy?.urlAction === item.key
                      )[0]?.label
                    }
                    classes="shareLinkModal-urlAction"
                    buttonClick={key}
                  >
                    {UNAUTH_LINK_TYPE.filter(
                      (item1) =>
                        !item1.key.includes("WEBUSING") || isAvailblePreview
                    ).map((item) => (
                      <DropdownItem
                        checkColor={
                          linkInfo?.fileShareLinkPolicy?.urlAction === item.key
                        }
                        label={item.label}
                        onClick={() =>
                          handleSetFileLinkPolicy("urlAction", item.key)
                        }
                      />
                    ))}
                  </Dropdown>
                )}
                <Typography
                  type="caption_rg"
                  classes="mg ml-10"
                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                >
                  {linkInfo?.fileShareLinkPolicy?.authType === "LOGIN"
                    ? t("file.link.authInfo")
                    : t("file.link.noAuthInfo")}
                </Typography>
              </Box>
            )}
            <Box classes="shareLinkModal-link">
              <Tooltip
                message={linkInfo?.actionUrl}
                maxWidth={"calc(100% - 115px)"}
              >
                <Typography
                  ellipsis
                  type="body2_rg"
                  exactColor={getColor("COLOR_TEXT_BRAND")}
                >
                  {linkInfo?.actionUrl}
                </Typography>
              </Tooltip>
              <Button
                btnStyle="link"
                label={t("file.link.copy")}
                classes="mg ml-auto"
                textColor={getColor("COLOR_TEXT_BRAND")}
                onClick={handleCopyLink}
                leadingIcon={
                  <Icons
                    variant="line"
                    label="link"
                    stroke={getColor("COLOR_TEXT_BRAND")}
                  />
                }
              />
            </Box>
            {linkInfo?.fileShareLinkPolicy?.authType !== "LOGIN" &&
              (isMyDoc ? (
                <Box direction="column">
                  <div className="shareLinkModal-divider" />
                  <Box alignItems="center">
                    <Typography type="subtitle2">
                      {t("file.link.setPw")}
                    </Typography>
                    <Toggle
                      checked={passwordInfo.isOn}
                      onChange={() => {
                        const random4Digit = Math.floor(
                          1000 + Math.random() * 9000
                        );
                        if (passwordInfo.isOn) {
                          handleSetFileLinkPolicy("password", "");
                        }
                        changePasswordInfo({
                          isOn: !passwordInfo.isOn,
                          pw: !passwordInfo.isOn ? random4Digit + "" : "",
                        });
                        changeIsPWChange(false);
                        handleSetFileLinkPolicy(
                          "password",
                          !passwordInfo.isOn ? random4Digit + "" : ""
                        );
                      }}
                      classes="mg ml-auto"
                    />
                  </Box>
                  <Box classes="mg mt-8" width="100%">
                    <Box direction="column">
                      <InputField
                        maxLength={4}
                        type={"positive-integer"}
                        placeholder={t("file.link.setPwPlaceholder")}
                        disabled={!passwordInfo.isOn}
                        value={passwordInfo.pw}
                        onChange={(e) => {
                          changePasswordInfo({
                            ...passwordInfo,
                            pw: e.target.value,
                          });
                          changeIsPWChange(false);
                        }}
                        trailingIcon={
                          <Box alignItems={"center"}>
                            {passwordInfo?.pw &&
                              passwordInfo?.pw?.length > 0 && (
                                <span
                                  className={"cursor pointer"}
                                  onClick={() =>
                                    changePasswordInfo({
                                      ...passwordInfo,
                                      pw: "",
                                    })
                                  }
                                >
                                  <Icons
                                    variant="line"
                                    label="closeLine"
                                    stroke={"#717985"}
                                  />
                                </span>
                              )}
                          </Box>
                        }
                        size="md"
                        width="399px"
                      />
                      {isPWChange && (
                        <Box classes={"mg mt-8"} alignItems="center">
                          <Icons
                            variant="line"
                            label="checkCircle"
                            stroke={getColor("COLOR_TEXT_BRAND")}
                            size={20}
                          />
                          <Typography
                            type="body2_rg"
                            classes={"mg ml-2"}
                            exactColor={getColor("COLOR_TEXT_BRAND")}
                          >
                            {t("file.link.setPwSuccess")}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Button
                      label={t("common.apply")}
                      btnStyle="fill"
                      state={
                        !passwordInfo.isOn ||
                        passwordInfo?.pw?.length === 0 ||
                        linkInfo?.fileShareLinkPolicy.password ===
                          passwordInfo.pw
                          ? "disable"
                          : "default"
                      }
                      onClick={() => {
                        handleSetFileLinkPolicy(
                          "password",
                          passwordInfo.isOn && passwordInfo.pw
                            ? passwordInfo.pw
                            : ""
                        );
                      }}
                      type="secondary"
                      classes="mg ml-8"
                    />
                  </Box>
                  <Box alignItems="center" classes="mg mt-28">
                    <Typography type="subtitle2">
                      {t("file.setEndDate")}
                    </Typography>
                    <Toggle
                      checked={expiredDateInfo.isOn}
                      onChange={() => {
                        changeExpiredDateInfo({
                          isOn: !expiredDateInfo.isOn,
                          date: !expiredDateInfo.isOn
                            ? new Date(dayjs().add(30, "day").valueOf())
                            : undefined,
                        });
                        handleSetFileLinkPolicy(
                          "allowDays",
                          expiredDateInfo.isOn ? -1 : 30
                        );
                      }}
                      classes="mg ml-auto"
                    />
                  </Box>
                  <Calendar
                    type="single"
                    selected={expiredDateInfo.date}
                    disabled={!expiredDateInfo.isOn}
                    minDate={new Date()}
                    placeholder={t("file.link.tooltip.setDueDate")}
                    hasTimezone
                    popperPlacement={"top-start"}
                    onSubmit={(e) => {
                      changeExpiredDateInfo({
                        ...expiredDateInfo,
                        date: e instanceof Date ? e : e.start,
                      });
                      handleSetFileLinkPolicy(
                        "allowDays",
                        dayjs(e instanceof Date ? e : e.start).diff(
                          dayjs().set("hour", 0),
                          "day"
                        ) + 1
                      );
                    }}
                    width={240}
                    classes="mg mt-8"
                  />
                </Box>
              ) : (
                (passwordInfo.isOn || expiredDateInfo.isOn) && (
                  <Box direction="column">
                    <div className="shareLinkModal-divider" />
                    {passwordInfo.isOn && (
                      <Box classes="mg mb-16">
                        <Typography width={96} type="subtitle2">
                          {t("login.login.password")}
                        </Typography>
                        <Typography type="body2_rg">
                          {passwordInfo.pw}
                        </Typography>
                      </Box>
                    )}
                    {expiredDateInfo.isOn && (
                      <Box alignItems="center">
                        <Typography width={96} type="subtitle2">
                          {t("file.link.tooltip.setDueDate")}
                        </Typography>
                        <Typography type="body2_rg">
                          {utilsCommon.renderDateFormat(
                            dayjs(expiredDateInfo.date).valueOf(),
                            "YYYY-MM-DD"
                          )}
                        </Typography>
                        <Typography
                          type="body2_rg"
                          classes="mg ml-4"
                        >{`(GMT+${serverTimezone})`}</Typography>
                      </Box>
                    )}
                  </Box>
                )
              ))}
          </Box>
          <Box height={32} />
        </Box>
      }
    />
  );
};
export default ShareLinkModal;
